import { type RefObject, useCallback, useRef } from 'react';
import type { Measurable } from '@radix-ui/rect';

import {
  EmbedSettingsForm,
  type EmbedSettingsFormHandle,
} from '@/components/blocks/shared/embed-settings-form';
import { FloatWrapControl } from '@/components/page-editor/settings-panel/float-wrap-control';
import type { GroupChildFloat } from '@/lib/blocks/layout/group-layout';
import { SettingsPopoverShell } from './settings-popover-shell';

export interface EmbedSettingsPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchorRef?: RefObject<Measurable | null>;
  url: string;
  html: string;
  float?: GroupChildFloat | null;
  onFloatChange?: (float: GroupChildFloat | null) => void;
  onSave: (url: string, html: string) => void;
  onCancel?: () => void;
}

export function EmbedSettingsPopover({
  open,
  onOpenChange,
  anchorRef,
  url,
  html,
  float = null,
  onFloatChange,
  onSave,
  onCancel = () => {},
}: EmbedSettingsPopoverProps) {
  const formRef = useRef<EmbedSettingsFormHandle | null>(null);

  const handleSave = useCallback(() => {
    const values = formRef.current?.getValues();
    if (!values) return;
    onSave(values.url, values.html);
  }, [onSave]);

  return (
    <SettingsPopoverShell
      open={open}
      onOpenChange={onOpenChange}
      anchorRef={anchorRef}
      title="Embed Settings"
      trackMovingAnchor={Boolean(onFloatChange)}
      onSave={handleSave}
      onCancel={onCancel}
    >
      <div className="space-y-6">
        <EmbedSettingsForm initialUrl={url} initialHtml={html} resetKey={open} ref={formRef} />
        {onFloatChange && (
          <FloatWrapControl
            value={float}
            onChange={onFloatChange}
            description="Float this embed so surrounding content in the same group wraps around it"
          />
        )}
      </div>
    </SettingsPopoverShell>
  );
}
